import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/api';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';
import ConfirmModal from './modals/ConfirmModal';

const SiteAssigner = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [draggedUser, setDraggedUser] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Custom modal states
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  
  // Page Tips collapsed state
  const [isTipsCollapsed, setIsTipsCollapsed] = useState(true);
  
  // Site unassignment warning modal state
  const [unassignWarning, setUnassignWarning] = useState({
    show: false,
    user: null,
    siteId: null,
    siteName: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/admin/sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSites();
  }, []);

  const handleDragStart = (e, user) => {
    setDraggedUser(user);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', user.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e, site) => {
    e.preventDefault();
    setDropTarget(site.id);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDropTarget(null);
  };

  const handleDrop = async (e, site) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedUser) return;

    try {
      // Check if user is already assigned to this site
      const isAlreadyAssigned = draggedUser.assigned_sites && draggedUser.assigned_sites.includes(site.id);
      
      if (isAlreadyAssigned) {
        setErrorModal({
          show: true,
          message: `${draggedUser.full_name} is already assigned to ${site.site_name}.`
        });
        return;
      }

      // Add site to user's assignments
      const updatedSites = [...(draggedUser.assigned_sites || []), site.id];
      
      await axios.put(`${API}/admin/users/${draggedUser.id}`, {
        assigned_sites: updatedSites
      });

      // Refresh users to show updated assignments
      fetchUsers();
      
      // Show success message
      setSuccessModal({
        show: true,
        message: `${draggedUser.full_name} has been assigned to ${site.site_name}`
      });
      
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to assign user to site: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
    
    setDraggedUser(null);
  };

  // Show warning before removing assignment
  const handleRemoveAssignmentClick = (user, siteId) => {
    const site = sites.find(s => s.id === siteId);
    setUnassignWarning({
      show: true,
      user: user,
      siteId: siteId,
      siteName: site?.site_name || 'this site'
    });
  };

  // Confirm and execute the removal
  const confirmRemoveAssignment = async () => {
    const { user, siteId, siteName } = unassignWarning;
    
    // Close the warning modal first
    setUnassignWarning({ show: false, user: null, siteId: null, siteName: '' });
    
    try {
      const updatedSites = user.assigned_sites.filter(id => id !== siteId);
      
      await axios.put(`${API}/admin/users/${user.id}`, {
        assigned_sites: updatedSites
      });

      fetchUsers();
      
      setSuccessModal({
        show: true,
        message: `${user.full_name} has been removed from ${siteName}`
      });
      
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to remove site assignment: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  // Cancel the removal
  const cancelRemoveAssignment = () => {
    setUnassignWarning({ show: false, user: null, siteId: null, siteName: '' });
  };

  return (
    <>
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
        {/* Modern Header - Purple Gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">Site Assigner</h1>
          <p className="text-purple-200">Assign team members to sites using drag and drop interface</p>
        </div>
        
        <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-400">
              Drag users from the left panel onto sites on the right to assign them. Users can be assigned to multiple sites.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Users Panel */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                  </svg>
                </div>
                Users ({users.length})
              </h2>
              
              <div className="space-y-3 max-h-[538px] overflow-y-auto pr-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, user)}
                    className="flex items-center p-3 bg-slate-700/50 border border-slate-600 rounded-lg cursor-move hover:bg-slate-700 hover:border-purple-500/50 transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-medium mr-3 text-sm overflow-hidden flex-shrink-0">
                      {user.profile_photo_url ? (
                        <img 
                          src={user.profile_photo_url} 
                          alt={`${user.full_name}`} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <span className="flex items-center justify-center w-full h-full">
                          {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      )}
                      {user.profile_photo_url && (
                        <span className="items-center justify-center w-full h-full" style={{display: 'none'}}>
                          {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate">{user.full_name}</div>
                      <div className="text-sm text-gray-400">{user.role}</div>
                      <div className="text-xs text-gray-500">{user.department}</div>
                      
                      {/* User's Site Assignments */}
                      {user.assigned_site_names && user.assigned_site_names.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {user.assigned_site_names.map((siteName, index) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 group"
                            >
                              {siteName}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveAssignmentClick(user, user.assigned_sites[index]);
                                }}
                                className="ml-1 text-blue-400 hover:text-blue-200 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </div>
                  </div>
                ))}
                
                {users.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                    </svg>
                    No users available
                  </div>
                )}
              </div>
            </div>

            {/* Sites Panel */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                  </svg>
                </div>
                Sites ({sites.length})
              </h2>
              
              <div className="space-y-4 max-h-[538px] overflow-y-auto pr-2">
                {sites.map((site) => {
                  const assignedUsers = users.filter(user => 
                    user.assigned_sites && user.assigned_sites.includes(site.id)
                  );
                  
                  return (
                    <div
                      key={site.id}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, site)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, site)}
                      className={`p-4 border-2 border-dashed rounded-lg transition-all duration-200 ${
                        dropTarget === site.id 
                          ? 'border-green-400 bg-green-500/10 shadow-lg shadow-green-500/20 scale-[1.02]' 
                          : 'border-slate-600 bg-slate-700/30 hover:border-purple-500/50 hover:bg-slate-700/50'
                      }`}
                    >
                      {/* Site Header */}
                      <div className="flex items-start gap-3 mb-3">
                        {/* Site Thumbnail */}
                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-600 flex items-center justify-center">
                          {site.thumbnail_url ? (
                            <img 
                              src={site.thumbnail_url} 
                              alt={site.site_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-full h-full items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600 ${site.thumbnail_url ? 'hidden' : 'flex'}`}
                          >
                            <svg className="w-6 h-6 text-white/80" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                            </svg>
                          </div>
                        </div>
                        
                        {/* Site Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white mb-1">{site.site_name}</h3>
                          {site.description && (
                            <p className="text-sm text-gray-400 line-clamp-2">{site.description}</p>
                          )}
                        </div>
                        
                        {dropTarget === site.id && (
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Assigned Users */}
                      <div>
                        <div className="text-xs text-gray-500 mb-2">
                          Assigned Users ({assignedUsers.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {assignedUsers.length > 0 ? (
                            assignedUsers.map((user) => (
                              <div 
                                key={user.id}
                                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30 group"
                              >
                                <span>{user.full_name}</span>
                                <button
                                  onClick={() => handleRemoveAssignmentClick(user, site.id)}
                                  className="ml-2 text-green-400 hover:text-green-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                  </svg>
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="text-xs text-gray-500 italic bg-slate-600/50 px-3 py-2 rounded-full border border-slate-500/50">
                              Drop users here to assign
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {sites.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                    </svg>
                    No sites available
                    <p className="text-sm mt-2 text-gray-600">Create sites in the Site Manager to start assigning users</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Page Tips - Collapsible */}
          <div className="mt-6 bg-slate-700/50 rounded-lg border border-slate-600 overflow-hidden">
            <button
              onClick={() => setIsTipsCollapsed(!isTipsCollapsed)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors duration-200"
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-white">Page Tips</span>
              </div>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isTipsCollapsed ? '' : 'rotate-180'}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!isTipsCollapsed && (
              <div className="px-4 pb-4">
                <ul className="text-xs text-gray-400 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Drag users from the left panel and drop them onto sites on the right to assign them</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Users can be assigned to multiple sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Click the <span className="text-purple-300 font-medium">×</span> button next to assignments to remove them</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>User assignments are shown both in the user cards and site drop zones</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Modals */}
      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />

      {/* Site Unassignment Warning Modal */}
      <ConfirmModal
        isOpen={unassignWarning.show}
        onClose={cancelRemoveAssignment}
        onConfirm={confirmRemoveAssignment}
        title="Remove Site Assignment"
        message={`Removing ${unassignWarning.user?.full_name || 'this user'} from ${unassignWarning.siteName} will end any active clock-in sessions and unassign them from tasks on this site. Do you want to continue?`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="warning"
      />
    </>
  );
};

export default SiteAssigner;
